import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, ShieldAlert, Phone, Mail } from "lucide-react";
import { SITE } from "../../lib/site";
import { trackEvent } from "../../lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-xl border border-inkline bg-warmwhite px-4 py-3 text-[1rem] text-charcoal placeholder:text-charcoal/40 transition-colors focus:border-forest focus:outline-none";
const labelCls = "block text-[0.85rem] font-medium text-forest mb-1.5";

const TIMING = ["Today / urgent", "Within a few days", "Within a few weeks", "Planning ahead"];

export default function ProfessionalReferralForm() {
  const [form, setForm] = useState({
    professionalName: "",
    organization: "",
    role: "",
    phone: "",
    email: "",
    clientIdentifier: "",
    careNeed: "",
    targetArea: "",
    timing: TIMING[2],
    preferredContactMethod: "Phone",
    consent: false,
    website: "",
  });
  const [status, setStatus] = useState("idle");

  const set = (k) => (e) =>
    setForm((f) => ({
      ...f,
      [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value,
    }));

  async function submit(e) {
    e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    if (!form.consent) {
      toast.error("Please acknowledge the privacy note so we can follow up.");
      return;
    }
    setStatus("submitting");
    try {
      if (!form.website) {
        const { consent, website, ...payload } = form;
        await axios.post(`${API}/inquiries/professional`, payload);
      }
      trackEvent("professional_referral_submitted");
      setStatus("success");
    } catch (err) {
      setStatus("error");
      toast.error("Something went wrong. Please try again, or call Doris directly.");
    }
  }

  if (status === "success") {
    return (
      <div
        data-testid="professional-referral-success"
        className="rounded-2xl border border-ivory/20 bg-ivory/5 p-8 text-center md:p-10"
        role="status"
      >
        <CheckCircle2 className="mx-auto h-12 w-12 text-clay" aria-hidden="true" />
        <h3 className="mt-5 font-serif text-2xl font-medium text-ivory">
          Thank you. Your referral has been sent to CareCraft.
        </h3>
        <p className="mx-auto mt-4 max-w-md leading-relaxed text-ivory/75">
          Doris will follow up by your preferred contact method, typically within 24 hours.
        </p>
        <p className="mt-4 text-[0.85rem] text-ivory/60">
          Urgent referral? Call {SITE.phoneDisplay}.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="professional-referral-form" noValidate>
      <div className="mb-8 flex items-start gap-3 rounded-xl border border-clay/40 bg-clay-soft p-4">
        <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0 text-clay" aria-hidden="true" />
        <p className="text-[0.85rem] leading-relaxed text-charcoal/80">
          Please do not include sensitive medical information in this form. Share only a general care
          need and a non-sensitive identifier. A HIPAA-appropriate workflow has not yet been approved
          for this channel, so sensitive details can be discussed by phone.
        </p>
      </div>
      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label htmlFor="r-name" className={labelCls}>
            Your name
          </label>
          <input id="r-name" type="text" required data-testid="referral-professional-name" className={inputCls} value={form.professionalName} onChange={set("professionalName")} autoComplete="name" />
        </div>
        <div>
          <label htmlFor="r-org" className={labelCls}>
            Organization
          </label>
          <input id="r-org" type="text" required data-testid="referral-organization" className={inputCls} value={form.organization} onChange={set("organization")} autoComplete="organization" />
        </div>
        <div>
          <label htmlFor="r-role" className={labelCls}>
            Role
          </label>
          <input id="r-role" type="text" required data-testid="referral-role" className={inputCls} value={form.role} onChange={set("role")} placeholder="Social worker, case manager, nurse…" />
        </div>
        <div>
          <label htmlFor="r-phone" className={labelCls}>
            Phone
          </label>
          <input id="r-phone" type="tel" required data-testid="referral-phone" className={inputCls} value={form.phone} onChange={set("phone")} autoComplete="tel" />
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="r-email" className={labelCls}>
            Email
          </label>
          <input id="r-email" type="email" required data-testid="referral-email" className={inputCls} value={form.email} onChange={set("email")} autoComplete="email" />
        </div>
        <div>
          <label htmlFor="r-identifier" className={labelCls}>
            Patient/client initials or non-sensitive identifier
          </label>
          <input id="r-identifier" type="text" required data-testid="referral-identifier" className={inputCls} value={form.clientIdentifier} onChange={set("clientIdentifier")} placeholder="e.g. initials only" maxLength={20} />
        </div>
        <div>
          <label htmlFor="r-area" className={labelCls}>
            Target area
          </label>
          <input id="r-area" type="text" required data-testid="referral-target-area" className={inputCls} value={form.targetArea} onChange={set("targetArea")} placeholder="e.g. San Fernando Valley" />
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="r-need" className={labelCls}>
            General care need
          </label>
          <textarea id="r-need" required rows={3} data-testid="referral-care-need" className={inputCls} value={form.careNeed} onChange={set("careNeed")} placeholder="A general description. No clinical details, please." />
        </div>
        <fieldset className="sm:col-span-2">
          <legend className={labelCls}>Timing / urgency</legend>
          <div className="flex flex-wrap gap-2.5">
            {TIMING.map((t) => (
              <label
                key={t}
                className={`cursor-pointer rounded-full border px-4 py-2.5 text-[0.9rem] transition-colors ${
                  form.timing === t
                    ? "border-forest bg-forest text-ivory"
                    : "border-inkline bg-warmwhite text-charcoal/75 hover:border-forest/40"
                }`}
              >
                <input type="radio" name="timing" value={t} checked={form.timing === t} onChange={set("timing")} className="sr-only" />
                {t}
              </label>
            ))}
          </div>
        </fieldset>
        <fieldset className="sm:col-span-2">
          <legend className={labelCls}>Preferred contact method</legend>
          <div className="flex flex-wrap gap-2.5">
            {["Phone", "Email"].map((m) => (
              <label
                key={m}
                className={`cursor-pointer rounded-full border px-4 py-2.5 text-[0.9rem] transition-colors ${
                  form.preferredContactMethod === m
                    ? "border-forest bg-forest text-ivory"
                    : "border-inkline bg-warmwhite text-charcoal/75 hover:border-forest/40"
                }`}
              >
                <input type="radio" name="preferredContactMethod" value={m} checked={form.preferredContactMethod === m} onChange={set("preferredContactMethod")} className="sr-only" />
                {m}
              </label>
            ))}
          </div>
        </fieldset>
        <div className="hidden" aria-hidden="true">
          <label htmlFor="r-website">Website</label>
          <input id="r-website" type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} />
        </div>
        <div className="sm:col-span-2">
          <label className="flex max-w-xl cursor-pointer items-start gap-3 text-[0.85rem] leading-relaxed text-charcoal/70">
            <input type="checkbox" required data-testid="referral-consent" checked={form.consent} onChange={set("consent")} className="mt-1 h-4 w-4 shrink-0 accent-[#214A40]" />
            <span>
              I understand this form is not for sensitive medical information, and that CareCraft may
              contact me about this referral. See our{" "}
              <Link to="/privacy" className="stitch-underline text-forest">
                Privacy Policy
              </Link>
              .
            </span>
          </label>
        </div>
        <div className="sm:col-span-2">
          <button
            type="submit"
            data-testid="professional-referral-submit-button"
            disabled={status === "submitting"}
            className="inline-flex min-h-[52px] w-full items-center justify-center gap-2 rounded-full bg-forest px-8 py-3.5 font-medium text-ivory transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-forest-dark disabled:opacity-60 sm:w-auto"
          >
            {status === "submitting" ? "Sending…" : "Send Referral"}
          </button>
          {status === "error" && (
            <p role="alert" className="mt-4 text-[0.9rem] text-clay">
              Your referral couldn't be sent just now. Please try again, or call {SITE.phoneDisplay}.
            </p>
          )}
          <p className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-[0.85rem] text-charcoal/60">
            <a href={SITE.phoneHref} data-testid="referral-direct-phone" className="inline-flex items-center gap-2 text-forest hover:text-clay">
              <Phone className="h-4 w-4" aria-hidden="true" /> {SITE.phoneDisplay}
            </a>
            <a href={`mailto:${SITE.email}`} data-testid="referral-direct-email" className="inline-flex items-center gap-2 text-forest hover:text-clay">
              <Mail className="h-4 w-4" aria-hidden="true" /> {SITE.email}
            </a>
          </p>
        </div>
      </div>
    </form>
  );
}
